import React, { useState } from 'react'
import { format } from 'date-fns'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useTheme } from 'styled-components'
import { Pill, Tonality, MaterialIcon } from '@dsny/dsny-component-library'
import {
  AIFilled,
  PlayArrowFilled,
  Info,
  ExplicitFilled
} from '@dsny/dsny-component-library/icons'
import {
  useNoHoverMediaQuery,
  useTabletDownMediaQuery
} from 'utils/hooks/useMediaQueries'
import {
  RESPONSIVE_BREAKPOINT_VERSIONS,
  useResponsiveBreakpointsVersion
} from 'contexts/ResponsiveBreakpointsContext'
import {
  ReleaseCardContainer,
  ReleaseCardImage,
  ReleaseCardTextWrapper,
  ReleaseCardTitle,
  ReleaseArtistName,
  Row,
  ActionBar,
  ActionButton,
  ActionBarAbsoluteContainer,
  CardContent,
  GridCardActionsRow,
  GridMoreActions,
  ReleaseMarketTag
} from './ReleaseCard.styles'
import Tooltip from 'components/Tooltip'
import PlaceholderLogo from 'images/icons/placeholder.png'
import ReleaseDownloadDropdown from 'components/ReleaseDownloadDropdown/ReleaseDownloadDropdown'
import ReleaseContextMenu from 'components/Dropdowns/ReleaseMoreActionsDropdown/ReleaseContextMenu'
import ReleaseMoreActionsDropdown from 'components/Dropdowns/ReleaseMoreActionsDropdown/ReleaseMoreActionsDropdown'
import Badges from 'containers/ReleasesView/Badges/Badges'
import { DATE_FORMAT } from '../../utils/dateHelpers'
import useArchiveToFilter from 'utils/hooks/useArchiveToFilter'
import ArchivedArtworkOverlay from 'components/ArchivedArtworkOverlay/ArchivedArtworkOverlay'

export default function ReleaseCard({
  release,
  onShowModal,
  handlePlayRelease,
  handleDownloadRelease
}) {
  const { t } = useTranslation()
  const { enabled: archiveToFilterEnabled } = useArchiveToFilter()
  const theme = useTheme()
  const navigate = useNavigate()
  const responsiveBreakpointsVersion = useResponsiveBreakpointsVersion()
  const responsiveBreakpointsV2 =
    responsiveBreakpointsVersion === RESPONSIVE_BREAKPOINT_VERSIONS.V2
  const isTabletDown = useTabletDownMediaQuery()
  const hasNoHover = useNoHoverMediaQuery()
  const [isQuickActionsOpen, setIsQuickActionsOpen] = useState(false)
  const releaseInfoPath = `/releases/releaseInfo/${release.releaseid}`
  const isQuickActionsAlwaysVisible = isTabletDown || hasNoHover
  const onPlayRelease = (e) => {
    e.stopPropagation()
    handlePlayRelease(release)
  }
  const onDownloadRelease = (format) => {
    handleDownloadRelease(release, format)
  }

  const onInfoReleaseClick = (e) => {
    e.stopPropagation()
    navigate(releaseInfoPath)
  }

  const onReleaseClick = () => {
    navigate(releaseInfoPath)
  }

  return (
    <ReleaseContextMenu release={release}>
      <ReleaseCardContainer
        $responsiveBreakpointsV2={responsiveBreakpointsV2}
        data-cy="release-card"
        data-testid="release-card-click-target"
        data-release-id={release?.releaseid}
        onClick={() => (isTabletDown ? onReleaseClick() : onShowModal(release))}
      >
        <div
          style={{
            position: 'relative',
            width: '100%',
            aspectRatio: '1 / 1',
            maxWidth: 214
          }}
        >
          <ReleaseCardImage
            src={release?.thumbnail || PlaceholderLogo}
            alt="album cover"
          />
          {archiveToFilterEnabled && release.archived && (
            <ArchivedArtworkOverlay />
          )}

          {release?.markettags?.includes('cancon') && (
            <ReleaseMarketTag
              $responsiveBreakpointsV2={responsiveBreakpointsV2}
            >
              <Pill
                variant="tonal"
                mode="dark"
                label={t('release-list.canadian')}
                icon={<MaterialIcon name="CanadaFlag" />}
                style={{
                  padding: '2px 6px',
                  textTransform: 'uppercase',
                  width: 'auto',
                  maxWidth: '100px',
                  backgroundColor: theme.colors.neutralW30,
                  color: theme.colors.neutralW90
                }}
              />
            </ReleaseMarketTag>
          )}
          {!isTabletDown && (
            <ActionBarAbsoluteContainer className="action_bar">
              <ActionBar>
                <Tooltip
                  hasArrow={false}
                  placement="top"
                  text={t('release-list.play-release')}
                >
                  <ActionButton
                    $responsiveBreakpointsV2={responsiveBreakpointsV2}
                    data-cy="release-card-play"
                    variant="primary"
                    setting="player"
                    mode="dark"
                    onClick={onPlayRelease}
                  >
                    <PlayArrowFilled
                      width={16}
                      height={16}
                      color={theme.colors.black0}
                    />
                  </ActionButton>
                </Tooltip>

                <ReleaseDownloadDropdown
                  release={release}
                  onDownloadRelease={onDownloadRelease}
                />

                <Tooltip
                  hasArrow={false}
                  placement="top"
                  text={t('release-list.release-info')}
                >
                  <ActionButton
                    $responsiveBreakpointsV2={responsiveBreakpointsV2}
                    data-cy="release-card-info"
                    variant="primary"
                    setting="player"
                    mode="dark"
                    onClick={onInfoReleaseClick}
                  >
                    <Info width={16} height={16} color={theme.colors.black0} />
                  </ActionButton>
                </Tooltip>
              </ActionBar>
            </ActionBarAbsoluteContainer>
          )}
        </div>
        <CardContent $responsiveBreakpointsV2={responsiveBreakpointsV2}>
          <ReleaseCardTextWrapper
            $responsiveBreakpointsV2={responsiveBreakpointsV2}
          >
            <Tonality.UIText
              className="release_card_date"
              textStyle="caption"
              style={{ fontSize: 12, marginBottom: 4 }}
              color={theme.colors.neutralW70}
            >
              {format(new Date(release?.available), DATE_FORMAT)}
            </Tonality.UIText>
            <ReleaseCardTitle
              $isCompact={isTabletDown}
              $responsiveBreakpointsV2={responsiveBreakpointsV2}
              className="truncate"
            >
              {release?.title}
            </ReleaseCardTitle>
            <Row
              className="release_card_artist_row"
              style={{ marginTop: 4, alignItems: 'center' }}
            >
              {release?.explicit && (
                <ExplicitFilled
                  width={16}
                  height={16}
                  color={theme.colors.neutralW100}
                />
              )}
              {release?.is_ai_generated && (
                <AIFilled
                  width={16}
                  height={16}
                  color={theme.colors.neutralW100}
                />
              )}
              <ReleaseArtistName
                $responsiveBreakpointsV2={responsiveBreakpointsV2}
                className="truncate"
              >
                {release?.artist}
              </ReleaseArtistName>
            </Row>
          </ReleaseCardTextWrapper>

          <GridCardActionsRow
            $responsiveBreakpointsV2={responsiveBreakpointsV2}
          >
            <Badges release={release} />
            <GridMoreActions
              $isAlwaysVisible={isQuickActionsAlwaysVisible}
              $isOpen={isQuickActionsOpen}
              $responsiveBreakpointsV2={responsiveBreakpointsV2}
              className="grid_more_actions"
              data-always-visible={isQuickActionsAlwaysVisible}
              onClick={(event) => event.stopPropagation()}
            >
              <ReleaseMoreActionsDropdown
                listReleases={release}
                onOpenChange={setIsQuickActionsOpen}
                source="releases_view_release_grid_more_actions"
                variant="grid"
              />
            </GridMoreActions>
          </GridCardActionsRow>
        </CardContent>
      </ReleaseCardContainer>
    </ReleaseContextMenu>
  )
}

ReleaseCard.propTypes = {
  release: PropTypes.shape({
    title: PropTypes.string,
    artist: PropTypes.string
  })
}
